/** Draft schema version that Interface can author and hydrate. */
export const FORM_DRAFT_SCHEMA_VERSION = 1 as const;

export const FORM_FIELD_TYPES = [
  'shortText',
  'longText',
  'number',
  'date',
  'singleSelect',
  'multiSelect',
  'object',
  'array',
] as const;

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const FORM_EXTRACTION_MODELS = [
  'FAST',
  'REASONING_MINI',
  'REASONING_STANDARD',
  'REASONING_MAX',
] as const;

export type FormExtractionModel = (typeof FORM_EXTRACTION_MODELS)[number];
export const DEFAULT_FORM_EXTRACTION_MODEL: FormExtractionModel =
  'REASONING_MINI';

export const FORM_EXTRACTION_REASONING_EFFORTS = [
  'low',
  'medium',
  'high',
] as const;

export type FormExtractionReasoningEffort =
  (typeof FORM_EXTRACTION_REASONING_EFFORTS)[number];
export const DEFAULT_FORM_EXTRACTION_REASONING_EFFORT: FormExtractionReasoningEffort =
  'medium';

export type FormFieldActivationOperator =
  | 'equals'
  | 'notEquals'
  | 'answerInSelection'
  | 'answerNotInSelection'
  | 'hasValue'
  | 'doesNotHaveValue';

export interface FormFieldActivationCondition {
  fieldId: string;
  operator?: FormFieldActivationOperator | null;
  candidateValue?: string | null;
}

export function getOnlyActiveWhenOperators(
  fieldType: FormFieldType
): FormFieldActivationOperator[] {
  if (fieldType === 'singleSelect') return ['equals', 'notEquals'];
  if (fieldType === 'multiSelect') {
    return ['answerInSelection', 'answerNotInSelection'];
  }
  if (fieldType === 'shortText' || fieldType === 'longText') {
    return ['hasValue', 'doesNotHaveValue'];
  }
  return [];
}

export interface FormFieldOption {
  label: string;
  value: string;
}

export interface FormFieldValidation {
  maxLength?: number;
}

export interface FormFieldExtraction {
  prompt: string;
  model?: FormExtractionModel;
  reasoningEffort?: FormExtractionReasoningEffort;
}

export interface FormFieldExport {
  includeReasoning?: boolean;
  includeQuotes?: boolean;
  includeCitations?: boolean;
}

export const DEFAULT_FORM_FIELD_EXPORT = {
  includeReasoning: false,
  includeQuotes: false,
  includeCitations: false,
} as const;

export const SALESFORCE_DESTINATION_TYPES = [
  'string',
  'textarea',
  'picklist',
  'date',
] as const;

export type FormSalesforceDestinationType =
  (typeof SALESFORCE_DESTINATION_TYPES)[number];

export interface FormSalesforceDestination {
  type: FormSalesforceDestinationType;
  label: string;
  helpText: string;
  required: boolean;
  length: number | null;
  picklistValues: string[];
}

export interface FormSalesforceEvidenceExport
  extends Required<FormFieldExport> {
  fieldAPIName: string;
  type: 'string' | 'textarea';
}

export interface FormSalesforceFieldExport {
  fieldAPIName: string;
  destination: FormSalesforceDestination;
  sentAs: { formValue: string; salesforceValue: string }[];
  evidence?: FormSalesforceEvidenceExport;
}

/**
 * Questions whose answers this question reads as settled evidence. A section
 * reference expands on the server to that section's questions, except this
 * question, its children, and questions with their own dependsOn.
 */
export interface FormFieldDependsOn {
  fieldIds?: string[];
  sectionIds?: string[];
}

export interface FormField {
  /** Stable builder UUID, referenced by activation conditions and answers. */
  id: string;
  type: 'field';
  fieldType: FormFieldType;
  label: string;
  helpText?: string;
  options?: FormFieldOption[];
  validation?: FormFieldValidation;
  onlyActiveWhen?: FormFieldActivationCondition;
  dependsOn?: FormFieldDependsOn;
  extraction?: FormFieldExtraction;
  export?: FormFieldExport;
  exports?: { salesforce: FormSalesforceFieldExport };
  /** The only supported nested level. Child fields cannot have children. */
  children?: FormField[];
  /** Structural schemas are not independent questions. */
  properties?: FormField[];
  items?: FormField;
}

export interface FormSection {
  /** Opaque stable UUID minted by the builder. Never shown to authors. */
  id: string;
  type: 'section';
  label: string;
  children: FormField[];
}

export type FormNode = FormSection | FormField;

export interface FormDraftConfig {
  extractionContext: { prompt: string };
  /** Root nodes are sections only. Their order is the display/extraction order. */
  form: FormSection[];
  exports?: {
    salesforce: {
      objectAPIName: string;
      householdLookup: { fieldAPIName: string };
    };
  };
}

/** Read the same canonical configuration in the builder and published submissions. */
export function readFormConfig(
  schemaVersion: number,
  config: unknown
): FormDraftConfig | undefined {
  if (schemaVersion !== FORM_DRAFT_SCHEMA_VERSION || !isRecord(config))
    return undefined;
  const { extractionContext, form: rootNodes } = config;
  if (
    !isRecord(extractionContext) ||
    typeof extractionContext.prompt !== 'string' ||
    !Array.isArray(rootNodes)
  )
    return undefined;
  const sections = rootNodes.map(readFormSection);
  if (sections.some((section) => !section)) return undefined;
  const ids = new Set<string>();
  const claim = (id: string) => {
    if (ids.has(id)) return false;
    ids.add(id);
    return true;
  };
  // Stable schema IDs address both answers and activation references.
  for (const section of sections) {
    if (!section || !claim(section.id)) return undefined;
    const pending = [...section.children];
    while (pending.length) {
      const field = pending.pop()!;
      if (!claim(field.id)) return undefined;
      pending.push(...(field.children ?? []), ...(field.properties ?? []));
      if (field.items) pending.push(field.items);
    }
  }
  const exports = readRootExports(config.exports);
  if (config.exports !== undefined && !exports) return undefined;
  // Every section was checked above before converting the narrowed array.
  return {
    extractionContext: { prompt: extractionContext.prompt },
    form: sections as FormSection[],
    ...(exports ? { exports } : {}),
  };
}

function readFormSection(value: unknown): FormSection | undefined {
  if (
    !isRecord(value) ||
    value.type !== 'section' ||
    typeof value.id !== 'string' ||
    typeof value.label !== 'string' ||
    !Array.isArray(value.children)
  )
    return undefined;
  const children = value.children.map((child) => readFormField(child, true));
  if (children.some((child) => !child)) return undefined;
  // Each child passed the reader above.
  return {
    id: value.id,
    type: 'section',
    label: value.label,
    children: children as FormField[],
  };
}

function readFormField(
  value: unknown,
  allowChildren: boolean,
  structuralDepth = 0,
  seen = new WeakSet<object>()
): FormField | undefined {
  if (
    !isRecord(value) ||
    value.type !== 'field' ||
    typeof value.id !== 'string' ||
    !isFormFieldType(value.fieldType) ||
    typeof value.label !== 'string' ||
    !isOptionalString(value.helpText) ||
    !isFormFieldOptions(value.options) ||
    !isFormFieldValidation(value.validation) ||
    !isActivationCondition(value.onlyActiveWhen) ||
    !isDependsOn(value.dependsOn) ||
    !isFormFieldExtraction(value.extraction) ||
    !isFormFieldExport(value.export) ||
    (!allowChildren && value.children !== undefined) ||
    (value.children !== undefined && !Array.isArray(value.children))
  )
    return undefined;
  if (seen.has(value)) return undefined;
  seen.add(value);
  if (
    structuralDepth > 0 &&
    (value.children !== undefined ||
      value.export !== undefined ||
      value.onlyActiveWhen !== undefined ||
      value.dependsOn !== undefined ||
      value.extraction?.model !== undefined ||
      value.extraction?.reasoningEffort !== undefined ||
      value.exports !== undefined)
  )
    return undefined;
  let properties: FormField[] | undefined;
  let items: FormField | undefined;
  if (value.fieldType === 'object') {
    if (
      !Array.isArray(value.properties) ||
      !value.properties.length ||
      value.items !== undefined ||
      value.exports !== undefined
    )
      return undefined;
    const parsed = value.properties.map((child) =>
      readFormField(child, false, structuralDepth + 1, seen)
    );
    if (parsed.some((child) => !child)) return undefined;
    properties = parsed as FormField[];
  } else if (value.fieldType === 'array') {
    if (value.properties !== undefined || value.exports !== undefined)
      return undefined;
    items = readFormField(value.items, false, structuralDepth + 1, seen);
    if (!items) return undefined;
  } else if (value.properties !== undefined || value.items !== undefined) {
    return undefined;
  }
  const children = (value.children ?? []).map((child) =>
    readFormField(child, false, 0, seen)
  );
  if (children.some((child) => !child)) return undefined;
  const exports = readFieldExports(value.exports);
  if (value.exports !== undefined && !exports) return undefined;
  return {
    id: value.id,
    type: 'field',
    fieldType: value.fieldType,
    label: value.label,
    ...(value.helpText !== undefined ? { helpText: value.helpText } : {}),
    ...(value.options !== undefined ? { options: value.options } : {}),
    ...(value.validation !== undefined ? { validation: value.validation } : {}),
    ...(value.onlyActiveWhen !== undefined
      ? { onlyActiveWhen: value.onlyActiveWhen }
      : {}),
    ...(value.dependsOn !== undefined ? { dependsOn: value.dependsOn } : {}),
    ...(value.extraction !== undefined ? { extraction: value.extraction } : {}),
    ...(structuralDepth === 0
      ? { export: { ...DEFAULT_FORM_FIELD_EXPORT, ...value.export } }
      : {}),
    ...(exports ? { exports } : {}),
    ...(properties ? { properties } : {}),
    ...(items ? { items } : {}),
    // Each child passed the reader above.
    ...(children.length > 0 ? { children: children as FormField[] } : {}),
  };
}

function readRootExports(value: unknown): FormDraftConfig['exports'] {
  if (!isRecord(value) || !isRecord(value.salesforce)) return undefined;
  const { salesforce } = value;
  if (
    typeof salesforce.objectAPIName !== 'string' ||
    !isRecord(salesforce.householdLookup) ||
    typeof salesforce.householdLookup.fieldAPIName !== 'string'
  )
    return undefined;
  return {
    salesforce: {
      objectAPIName: salesforce.objectAPIName,
      householdLookup: {
        fieldAPIName: salesforce.householdLookup.fieldAPIName,
      },
    },
  };
}

function readFieldExports(value: unknown): FormField['exports'] {
  if (!isRecord(value) || !isRecord(value.salesforce)) return undefined;
  const { salesforce } = value;
  const destination = readSalesforceDestination(salesforce.destination);
  if (
    typeof salesforce.fieldAPIName !== 'string' ||
    !destination ||
    (salesforce.sentAs !== undefined && !Array.isArray(salesforce.sentAs))
  )
    return undefined;
  const sentAs = (salesforce.sentAs ?? []).map((entry) =>
    isRecord(entry) &&
    typeof entry.formValue === 'string' &&
    typeof entry.salesforceValue === 'string'
      ? { formValue: entry.formValue, salesforceValue: entry.salesforceValue }
      : undefined
  );
  const evidence =
    salesforce.evidence === undefined
      ? undefined
      : readSalesforceEvidence(salesforce.evidence);
  if (
    sentAs.some((entry) => !entry) ||
    (salesforce.evidence !== undefined && !evidence)
  )
    return undefined;
  return {
    salesforce: {
      fieldAPIName: salesforce.fieldAPIName,
      destination,
      sentAs: sentAs as FormSalesforceFieldExport['sentAs'],
      ...(evidence ? { evidence } : {}),
    },
  };
}

function readSalesforceEvidence(
  value: unknown
): FormSalesforceEvidenceExport | undefined {
  if (
    !isRecord(value) ||
    typeof value.fieldAPIName !== 'string' ||
    (value.type !== 'string' && value.type !== 'textarea') ||
    (value.includeReasoning !== undefined &&
      typeof value.includeReasoning !== 'boolean') ||
    (value.includeQuotes !== undefined &&
      typeof value.includeQuotes !== 'boolean') ||
    (value.includeCitations !== undefined &&
      typeof value.includeCitations !== 'boolean')
  )
    return undefined;
  return {
    fieldAPIName: value.fieldAPIName,
    type: value.type,
    includeReasoning: value.includeReasoning ?? false,
    includeQuotes: value.includeQuotes ?? false,
    includeCitations: value.includeCitations ?? false,
  };
}

function readSalesforceDestination(
  value: unknown
): FormSalesforceDestination | undefined {
  if (
    !isRecord(value) ||
    !SALESFORCE_DESTINATION_TYPES.some((type) => type === value.type) ||
    (value.label !== undefined && typeof value.label !== 'string') ||
    (value.helpText !== undefined && typeof value.helpText !== 'string') ||
    (value.required !== undefined && typeof value.required !== 'boolean') ||
    (value.length !== undefined &&
      value.length !== null &&
      typeof value.length !== 'number') ||
    (value.picklistValues !== undefined && !isStringArray(value.picklistValues))
  )
    return undefined;
  return {
    type: value.type as FormSalesforceDestinationType,
    label: value.label ?? '',
    helpText: value.helpText ?? '',
    required: value.required ?? false,
    length: value.length ?? null,
    picklistValues: value.picklistValues ?? [],
  };
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((entry) => typeof entry === 'string')
  );
}

function isFormFieldType(value: unknown): value is FormFieldType {
  return FORM_FIELD_TYPES.some((type) => type === value);
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isFormFieldOptions(
  value: unknown
): value is FormFieldOption[] | undefined {
  return (
    value === undefined ||
    (Array.isArray(value) &&
      value.every(
        (option) =>
          isRecord(option) &&
          typeof option.label === 'string' &&
          typeof option.value === 'string'
      ))
  );
}

function isFormFieldValidation(
  value: unknown
): value is FormFieldValidation | undefined {
  return (
    value === undefined ||
    (isRecord(value) &&
      (value.maxLength === undefined || typeof value.maxLength === 'number'))
  );
}

function isFormFieldExtraction(
  value: unknown
): value is FormFieldExtraction | undefined {
  return (
    value === undefined ||
    (isRecord(value) &&
      typeof value.prompt === 'string' &&
      (value.model === undefined || isFormExtractionModel(value.model)) &&
      (value.reasoningEffort === undefined ||
        isFormExtractionReasoningEffort(value.reasoningEffort)))
  );
}

function isFormExtractionModel(value: unknown): value is FormExtractionModel {
  return FORM_EXTRACTION_MODELS.some((model) => model === value);
}

function isFormExtractionReasoningEffort(
  value: unknown
): value is FormExtractionReasoningEffort {
  return FORM_EXTRACTION_REASONING_EFFORTS.some((effort) => effort === value);
}

function isFormFieldExport(
  value: unknown
): value is Partial<FormFieldExport> | null | undefined {
  return (
    value == null ||
    (isRecord(value) &&
      (value.includeReasoning === undefined ||
        typeof value.includeReasoning === 'boolean') &&
      (value.includeQuotes === undefined ||
        typeof value.includeQuotes === 'boolean') &&
      (value.includeCitations === undefined ||
        typeof value.includeCitations === 'boolean'))
  );
}

function isDependsOn(value: unknown): value is FormFieldDependsOn | undefined {
  if (value === undefined) return true;
  const isIdList = (ids: unknown) =>
    ids === undefined ||
    (Array.isArray(ids) && ids.every((id) => typeof id === 'string'));
  return (
    isRecord(value) && isIdList(value.fieldIds) && isIdList(value.sectionIds)
  );
}

function isActivationCondition(
  value: unknown
): value is FormFieldActivationCondition | undefined {
  if (value === undefined) return true;
  if (!isRecord(value) || typeof value.fieldId !== 'string') return false;
  const operator = value.operator;
  const validOperator =
    operator === undefined ||
    operator === null ||
    operator === 'equals' ||
    operator === 'notEquals' ||
    operator === 'answerInSelection' ||
    operator === 'answerNotInSelection' ||
    operator === 'hasValue' ||
    operator === 'doesNotHaveValue';
  return (
    validOperator &&
    (value.candidateValue == null || typeof value.candidateValue === 'string')
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
